import { Banner, BannerButtonProps, BannerData } from '@components/display/banners/Banner';
import { useCallback, useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 5;
const PREFETCH_THRESHOLD = 2;

type HighlightedBannerPage = {
    count: number;
    results: BannerData[];
};

type HighlightedBannerSectionProps<C extends React.ElementType = 'button'> = {
    fetchPage: (page: number, pageSize: number) => Promise<HighlightedBannerPage>;
    onError: (error: unknown) => void;
    buttonPropsForBanner?: (banner: BannerData | undefined) => BannerButtonProps<C>;
    className?: string;
    pageSize?: number;
    prefetchThreshold?: number;
};

const createLoadingBanner = (): BannerData => ({
    loading: true,
});

export const HighlightedBannerSection = <C extends React.ElementType = 'button'>({
    fetchPage,
    onError,
    buttonPropsForBanner,
    className,
    pageSize = PAGE_SIZE,
    prefetchThreshold = PREFETCH_THRESHOLD,
}: HighlightedBannerSectionProps<C>) => {
    const [isFetching, setIsFetching] = useState(false);
    const [banners, setBanners] = useState<BannerData[]>([]);
    const [count, setCount] = useState(0);
    const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);

    const loadedPagesRef = useRef<Set<number>>(new Set());
    const loadingPagesRef = useRef<Set<number>>(new Set());

    const fetchBannerPage = useCallback(
        async (page: number) => {
            if (page < 1) {
                return;
            }

            const totalPages = count > 0 ? Math.ceil(count / pageSize) : undefined;
            if (
                (totalPages && page > totalPages) ||
                loadedPagesRef.current.has(page) ||
                loadingPagesRef.current.has(page)
            ) {
                return;
            }

            loadingPagesRef.current.add(page);
            if (loadedPagesRef.current.size === 0) {
                setIsFetching(true);
            }

            try {
                const pageData = await fetchPage(page, pageSize);

                if (pageData.count === 0) {
                    setBanners([]);
                    setCount(0);
                } else {
                    setCount(pageData.count);
                    setBanners((previousBanners) => {
                        const next = Array.from(
                            { length: Math.max(pageData.count, previousBanners.length) },
                            (_, index) => previousBanners[index] ?? createLoadingBanner()
                        );
                        const startIndex = (page - 1) * pageSize;

                        pageData.results.forEach((banner, index) => {
                            next[startIndex + index] = banner;
                        });

                        return next.slice(0, pageData.count);
                    });
                }

                loadedPagesRef.current.add(page);
            } catch (error) {
                onError(error);
            } finally {
                loadingPagesRef.current.delete(page);
                if (loadedPagesRef.current.size > 0 || loadingPagesRef.current.size === 0) {
                    setIsFetching(false);
                }
            }
        },
        [count, fetchPage, onError, pageSize]
    );

    const loadBatchForSlide = useCallback(
        (index: number) => {
            if (index < 0) {
                return;
            }

            const targetPage = Math.floor(index / pageSize) + 1;
            fetchBannerPage(targetPage);

            const positionInPage = index % pageSize;
            if (positionInPage >= pageSize - prefetchThreshold) {
                fetchBannerPage(targetPage + 1);
            }
        },
        [fetchBannerPage, pageSize, prefetchThreshold]
    );

    useEffect(() => {
        if (loadedPagesRef.current.size === 0 && loadingPagesRef.current.size === 0) {
            setBanners(Array.from({ length: pageSize }, createLoadingBanner));
            fetchBannerPage(1);
        }
    }, [fetchBannerPage, pageSize]);

    useEffect(() => {
        if (banners.length > 0) {
            loadBatchForSlide(selectedSlideIndex);
        }
    }, [banners.length, loadBatchForSlide, selectedSlideIndex]);

    return banners.length > 0 ? (
        <Banner<C>
            loading={isFetching}
            data={{ items: banners, count }}
            className={className}
            onSlideIndexChange={setSelectedSlideIndex}
            buttonProps={buttonPropsForBanner?.(banners[selectedSlideIndex])}
        />
    ) : null;
};
